import {
  AddCareerRoleProfileSkillRequest,
  CareerRoleFullPictureResponse,
  SkillTopicItemDto,
  SkillWithResourcesResponse
} from '../../../../models';
import { TopicDependencyOption } from './add-profile-topic-dialog.component';

export interface CopyProfileSkillsResult {
  copied: number;
  skipped: number;
  failed: number;
  errors: string[];
}

export function resolveTopicItems(skill: SkillWithResourcesResponse): SkillTopicItemDto[] {
  if (!skill.topics?.length) {
    return [];
  }
  if (typeof skill.topics[0] === 'string') {
    return [];
  }
  return skill.topics as SkillTopicItemDto[];
}

export function getProfileSkills(
  picture: CareerRoleFullPictureResponse,
  level: string,
  specializationSlug?: string
): SkillWithResourcesResponse[] {
  if (specializationSlug) {
    const spec = picture.specializations.find((s) => s.slug === specializationSlug);
    return spec?.seniorityProfiles.find((p) => p.level === level)?.skills ?? [];
  }
  return picture.seniorityProfiles.find((p) => p.level === level)?.skills ?? [];
}

export function mapSkillToAddRequest(skill: SkillWithResourcesResponse): AddCareerRoleProfileSkillRequest | null {
  const topics = resolveTopicItems(skill);
  if (!topics.length) {
    return null;
  }

  return {
    skillId: skill.skill.id,
    dependsOn: (skill.dependsOn ?? []).map((dep) => ({
      skillId: dep.skillId,
      kind: dep.kind || 'hard'
    })),
    topics: topics.map((topic) => ({
      name: topic.name,
      evidenceMode: topic.evidenceMode,
      requiredProficiency: topic.level || 'intermediate',
      importance: topic.importance ?? 0.5,
      dependsOn: (topic.dependsOn ?? []).map((dep) => ({
        skillId: dep.skillId?.trim() ? dep.skillId : null,
        topicName: dep.topicName,
        kind: dep.kind || 'hard'
      }))
    }))
  };
}

export function topicDependencyLabel(skillName: string, topicName: string): string {
  return `${skillName} / ${topicName}`;
}

export function collectTopicOptionsFromProfile(skills: SkillWithResourcesResponse[]): TopicDependencyOption[] {
  const seen = new Set<string>();
  const options: TopicDependencyOption[] = [];
  for (const skillItem of skills) {
    for (const topic of resolveTopicItems(skillItem)) {
      const label = topicDependencyLabel(skillItem.skill.name, topic.name);
      if (seen.has(label)) {
        continue;
      }
      seen.add(label);
      options.push({
        skillId: skillItem.skill.id,
        skillName: skillItem.skill.name,
        topicName: topic.name,
        label
      });
    }
  }
  return options;
}

export function mergeTopicDependencyOptions(
  target: TopicDependencyOption[],
  extra: TopicDependencyOption[]
): TopicDependencyOption[] {
  const seen = new Set(target.map((o) => o.label));
  const merged = [...target];
  for (const option of extra) {
    if (seen.has(option.label)) {
      continue;
    }
    seen.add(option.label);
    merged.push(option);
  }
  return merged;
}

export function resolveTopicDependsOnKeys(
  topic: SkillTopicItemDto,
  options: TopicDependencyOption[],
  ownSkillId: string,
  ownSkillName: string
): string[] {
  if (!topic.dependsOn?.length) {
    return [];
  }
  const keys: string[] = [];
  for (const dep of topic.dependsOn) {
    const skillId = dep.skillId?.trim() || ownSkillId;
    const isSameSkill = !dep.skillId?.trim() || dep.skillId === ownSkillId;
    const match = options.find(
      (o) =>
        o.topicName.localeCompare(dep.topicName, undefined, { sensitivity: 'accent' }) === 0 &&
        (isSameSkill ? o.skillId === ownSkillId : o.skillId === skillId)
    );
    if (match) {
      keys.push(match.label);
      continue;
    }
    const skillName = isSameSkill
      ? ownSkillName
      : (options.find((o) => o.skillId === skillId)?.skillName ?? '');
    if (!skillName) {
      continue;
    }
    const label = topicDependencyLabel(skillName, dep.topicName);
    if (options.some((o) => o.label === label)) {
      keys.push(label);
    }
  }
  return keys;
}

export function isSkillCopyConflict(error: unknown): boolean {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = Number((error as { status: number }).status);
    if (status === 409) {
      return true;
    }
    const body = (error as { error?: { title?: string } }).error;
    const title = body?.title ?? '';
    if (/already/i.test(title) || /conflict/i.test(title)) {
      return true;
    }
  }
  const message =
    error && typeof error === 'object' && 'message' in error
      ? String((error as { message: string }).message)
      : String(error ?? '');
  return /already/i.test(message) || /conflict/i.test(message);
}
